import { Card, CardContent } from '@mlm/ui';
import type { RankProgress } from '../../lib/api';
import { fmtNumber } from '../../lib/format';

function pct(have: number, need: number): number {
  if (need <= 0) return 100;
  return Math.max(0, Math.min(100, (have / need) * 100));
}

function Bar({ label, have, need }: { label: string; have: number; need: number }) {
  const p = pct(have, need);
  const met = have >= need;
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-caption text-muted-foreground">{label}</span>
        <span className={`text-caption tabular-nums ${met ? 'text-emerald-600' : 'text-foreground'}`}>
          {fmtNumber(have)} / {fmtNumber(need)}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${met ? 'bg-emerald-500' : 'bg-primary'}`}
          style={{ width: `${p}%` }}
        />
      </div>
    </div>
  );
}

/**
 * "Progress to <next rank>" — shows the slot's current rank and how close it is
 * to the next one (PPV / GPV / qualifying legs). Rendered only when stairstep is
 * active and a higher rank exists.
 */
export function RankProgressPanel({ progress }: { progress: RankProgress }) {
  const next = progress.nextRank;
  if (!next) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Rank progress
          </p>
          <p className="mt-3 text-body">
            <span className="font-semibold capitalize">{progress.currentRankCode ?? 'Unranked'}</span>{' '}
            — you're at the top rank.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
          Progress to <span className="capitalize">{next.code}</span>
        </p>
        <p className="mt-1 text-caption text-muted-foreground">
          Current rank:{' '}
          <span className="capitalize text-foreground">{progress.currentRankCode ?? 'Unranked'}</span>
        </p>
        <div className="mt-3 space-y-3">
          <Bar label="Personal PV" have={Number(progress.currentPpv)} need={Number(next.minPpv)} />
          <Bar label="Group PV" have={Number(progress.currentGpv)} need={Number(next.minGpv)} />
          {next.minLegs > 0 && (
            <Bar
              label={next.minLegRank ? `Legs (rank ${next.minLegRank}+)` : 'Qualifying legs'}
              have={next.qualifyingLegs}
              need={next.minLegs}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
